/**
 * createPropertySchema — SCRUM-51 step 4 (interface-smith GREEN).
 *
 * Zod schema for POST /properties request body. Maps to DS-007 v8 §3.6
 * property fields. Used for DTO validation at the HTTP boundary.
 *
 * Enums mirror domain VOs exactly:
 *   - unitCategory: 'home' | 'condominium' | 'villa' | 'cabin' | 'townhome' | 'apartment' | 'other'
 *   - listingType: 'entire_unit' | 'private_room' | 'shared_room' | 'other' | null
 *   - status.billing: 'active' | 'inactive'
 *   - status.visibility: 'bookable' | 'non_bookable'
 *
 * The combination (billing='inactive', visibility='bookable') is a domain
 * hard invariant. PR 116 cycle-5 H1: this schema now rejects that pair via
 * z.refine at the HTTP boundary so an invalid combo surfaces as a clean 400
 * instead of leaking from the domain VO as a generic Error → 500. The
 * domain VO + DB CHECK remain the authoritative gates (defense in depth).
 */
import { z } from 'zod';
import { LocalTimeSchema } from '../time/local-time.js';
const bathroomsSchema = z.object({
    full: z.number().int().min(0),
    half: z.number().int().min(0),
    showerOnly: z.number().int().min(0),
    bathtubOnly: z.number().int().min(0),
});
const capacitySchema = z
    .object({
    maxSleeps: z.number().int().min(1),
    maxAdults: z.number().int().min(1),
    // SCRUM-210 §11.4 (H-A): standardCapacity is REQUIRED (no default — a wrong
    // default overcharges every guest). Integer >= 1; cross-field <= maxSleeps
    // is enforced by the .refine below. Mirrors the domain VO + DB CHECK.
    standardCapacity: z.number().int().min(1),
    allowsChildren: z.boolean(),
    allowsPets: z.boolean(),
    bedrooms: z.number().int().min(0),
    bathrooms: bathroomsSchema,
    // PR 116 cycle-4 H1: use LocalTimeSchema so invalid wire values
    // ("24:00", "12:60", "ab:cd") fail at Zod with a clean 400, instead of
    // bubbling up as a RangeError from createLocalTime() at runtime → 500.
    checkInOverride: LocalTimeSchema.nullable().optional(),
    checkOutOverride: LocalTimeSchema.nullable().optional(),
})
    .refine((c) => c.standardCapacity <= c.maxSleeps, {
    message: 'standardCapacity must be ≤ maxSleeps',
    path: ['standardCapacity'],
});
// PR 116 cycle-4 H2: bedType is the 8-value BED_TYPES vocabulary the domain
// enforces; pin it at the HTTP boundary so unsupported client input fails as
// a 400 rather than a generic domain Error → 500.
const BED_TYPE_VALUES = [
    'king',
    'queen',
    'full',
    'twin',
    'sofa_bed',
    'bunk_bed',
    'crib',
    'other',
];
const addressSchema = z.object({
    line1: z.string().min(1).max(255),
    line2: z.string().max(255).optional(),
    city: z.string().min(1).max(255),
    stateOrProvince: z.string().min(1).max(255),
    postalCode: z.string().min(1).max(20),
    countryCode: z.string().length(2),
    timezone: z.string().min(1),
});
// PR 116 cycle-5 H1: hard-invariant rejection at the HTTP boundary.
// (inactive, bookable) is forbidden in the domain (UnitStatus.create throws
// a plain Error). Catching it here means a malformed wire payload returns
// 400 with a structured Zod issue, instead of 500 from an uncaught domain
// Error. The domain VO + DB CHECK stay authoritative.
const unitStatusSchema = z
    .object({
    billing: z.enum(['active', 'inactive']),
    visibility: z.enum(['bookable', 'non_bookable']),
})
    .refine((s) => !(s.billing === 'inactive' && s.visibility === 'bookable'), {
    message: 'forbidden status combination: an inactive property cannot be bookable',
    path: ['visibility'],
});
export const createPropertySchema = z
    .object({
    unitName: z.string().min(1).max(255),
    unitCategory: z.enum([
        'home',
        'condominium',
        'villa',
        'cabin',
        'townhome',
        'apartment',
        'other',
    ]),
    listingType: z
        .enum(['entire_unit', 'private_room', 'shared_room', 'other'])
        .nullable()
        .optional(),
    status: unitStatusSchema,
    // PR 116 cycle-4 M1: count Unicode codepoints (matches domain VO's
    // char_length), not UTF-16 code units. `[...s].length` iterates the
    // string by codepoint so a 500-codepoint emoji string (which would be
    // up to 1000 UTF-16 units) passes — same behavior as the domain VO.
    description: z
        .string()
        .nullable()
        .optional()
        .refine((s) => s === null || s === undefined || [...s].length <= 500, {
        message: 'description must be at most 500 Unicode codepoints',
    }),
    unitAlias: z.string().max(255).nullable().optional(),
    websiteNameOverride: z.string().max(255).nullable().optional(),
    address: addressSchema,
    gpsCoordinates: z
        .object({
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
    })
        .nullable()
        .optional(),
    capacity: capacitySchema,
    bedroomConfig: z
        .array(z.object({
        bedroomIndex: z.number().int().min(1),
        beds: z.array(z.object({
            // PR 116 cycle-4 H2: pin to the 8 BED_TYPES values.
            bedType: z.enum(BED_TYPE_VALUES),
            quantity: z.number().int().min(1),
        })),
    }))
        .nullable()
        .optional(),
})
    .strip();
//# sourceMappingURL=create-property.schema.js.map